import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, tokenStore, setUnauthorisedHandler } from './api';
import { themeForSession } from './theme';

export type Role = 'ADMIN' | 'SALES_MANAGER' | 'SALES_ADMIN' | 'WAREHOUSE';

export interface CurrentUser {
  id: string;
  email: string;
  name: string;
  role: Role;
  totpEnabled: boolean;
  mustChangePassword: boolean;
  lastLoginAt: string | null;
}

interface Session {
  user: CurrentUser;
  permissions: string[];
  modules: string[];
  dashboardTiles: string[];
  availableTiles: string[];
  /** The idle window, so the client can warn before the server closes it. */
  session?: { idleMinutes: number; expiresAt: string | null; rememberDeviceDays?: number };
}

/** Why a session ended, so the sign-in page can say so. */
/** `revoked`: the server has already ended the session (a password change), so there is nothing to log out of. */
export type SignOutReason = 'manual' | 'inactivity' | 'revoked';

interface AuthValue extends Partial<Session> {
  isLoading: boolean;
  isAuthenticated: boolean;
  signIn: (email: string, password: string, totpCode?: string, backupCode?: string, rememberDevice?: boolean) =>
    Promise<{
      requiresTwoFactor?: boolean;
      /** How long "Remember this device" lasts; 0 means it is not offered. */
      rememberDeviceDays?: number;
      mustChangePassword?: boolean;
      requiresTwoFactorSetup?: boolean;
    }>;
  signOut: (reason?: SignOutReason) => Promise<void>;
  refresh: () => Promise<void>;
  /** Route/feature gate — mirrors the server's `can()` exactly. */
  can: (permission: string) => boolean;
  /**
   * Whether this role may see cost, landing cost and margin.
   * The server strips these fields regardless; this only avoids rendering
   * empty columns for roles that will never receive the data.
   */
  canSeeCost: boolean;
  /** Whether this role may see selling prices. False for Warehouse only. */
  canSeePricing: boolean;
}

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const load = async () => {
    if (!tokenStore.get()) {
      setSession(null);
      setIsLoading(false);
      return;
    }
    try {
      setSession(await api.get<Session>('/api/auth/me'));
    } catch {
      setSession(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setUnauthorisedHandler(() => setSession(null));
    void load();
  }, []);

  // A dark choice applies once signed in, and the sign-in page is light.
  useEffect(() => {
    if (!isLoading) themeForSession(!!session);
  }, [session, isLoading]);

  const value = useMemo<AuthValue>(() => {
    const permissions = session?.permissions ?? [];
    return {
      ...session,
      isLoading,
      isAuthenticated: !!session,
      can: (permission: string) => permissions.includes(permission),
      canSeeCost: permissions.includes('financial.cost_data'),
      canSeePricing: session?.user.role !== 'WAREHOUSE',

      signIn: async (email, password, totpCode, backupCode, rememberDevice) => {
        const res = await api.post<{
          token?: string;
          requiresTwoFactor?: boolean;
          rememberDeviceDays?: number;
          mustChangePassword?: boolean;
          requiresTwoFactorSetup?: boolean;
        }>('/api/auth/login', { email, password, totpCode, backupCode, rememberDevice });

        if (res.requiresTwoFactor) return { requiresTwoFactor: true, rememberDeviceDays: res.rememberDeviceDays ?? 0 };
        if (res.token) {
          tokenStore.set(res.token);
          await load();
        }
        return { mustChangePassword: res.mustChangePassword, requiresTwoFactorSetup: res.requiresTwoFactorSetup };
      },

      signOut: async (reason: SignOutReason = 'manual') => {
        if (reason !== 'revoked') await api.post('/api/auth/logout').catch(() => undefined);
        tokenStore.clear();
        setSession(null);
        // Read once by the sign-in page. Session storage rather than a route
        // param so it survives the redirect without ending up in a URL a user
        // might bookmark or share.
        if (reason === 'inactivity') {
          sessionStorage.setItem('vf_signout_reason', 'inactivity');
        }
      },

      refresh: load,
    };
  }, [session, isLoading]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
